import React from 'react';
import { AlertTriangle, CheckCircle2, ShieldAlert, XCircle } from 'lucide-react';
import {
  hasBlockingChecks,
  parseCheckAcknowledgements,
  parseInvoiceChecks,
  unacknowledgedWarnings,
  type InvoiceCheck,
} from '../utils/incomingInvoiceChecks';
import type { InvoiceDocument } from '../types';

function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

async function postJson(url: string, body: unknown): Promise<{ ok: boolean; payload: any }> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const payload = await res.json().catch(() => ({}));
  return { ok: res.ok, payload };
}

/** Kennzeichen der Migration 20261017120100 für Bestandsbelege, die ohne Prüfung gebucht wurden. */
export const LEGACY_AUTO_BOOKED_NOTE = 'legacy_auto_booked';

/** Feld → Schwere des schlimmsten Befunds (für Markierungen im Formular). */
export function checkToneByField(row: Pick<InvoiceDocument, 'checks' | 'checks_acknowledged' | 'approved_at'>): Record<string, 'error' | 'warning'> {
  if (row.approved_at) return {};
  const checks = parseInvoiceChecks(row.checks);
  const open = new Set(unacknowledgedWarnings(checks, parseCheckAcknowledgements(row.checks_acknowledged)).map((c) => c.code));
  const out: Record<string, 'error' | 'warning'> = {};
  for (const check of checks) {
    if (!check.field) continue;
    if (check.severity === 'error') out[check.field] = 'error';
    else if (open.has(check.code) && out[check.field] !== 'error') out[check.field] = 'warning';
  }
  return out;
}

/**
 * Gemeinsamer Zustand für Befunde (im Formular) und Kopfleiste (Status + Aktionen).
 * Aus Sage übernommene Belege und abgelehnte/stornierte Belege haben keine Prüfung.
 */
export function useIncomingInvoiceReview(params: {
  row: InvoiceDocument;
  currentUserId: string | null;
  onReviewed: (row: InvoiceDocument) => void;
}) {
  const { row } = params;
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [info, setInfo] = React.useState<string | null>(null);

  React.useEffect(() => {
    setError(null);
    setInfo(null);
  }, [row.id]);

  const checks = parseInvoiceChecks(row.checks);
  const acks = parseCheckAcknowledgements(row.checks_acknowledged);
  const openWarnings = unacknowledgedWarnings(checks, acks);
  const approved = Boolean(row.approved_at);
  const inReview = row.status === 'draft';
  const applies = row.source !== 'sage_import' && (inReview || approved) && !['rejected', 'cancelled'].includes(row.status);
  const firstApprovedByMe = Boolean(row.approval_first_by && params.currentUserId && row.approval_first_by === params.currentUserId);

  const run = async (path: string, body: unknown, successInfo?: (payload: any) => string | null) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const { ok, payload } = await postJson(`/api/invoices/${encodeURIComponent(row.id)}/${path}`, body);
      if (!ok) throw new Error(payload?.error || 'Aktion fehlgeschlagen.');
      if (payload?.item) params.onReviewed(payload.item as InvoiceDocument);
      setInfo(successInfo ? successInfo(payload) : null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aktion fehlgeschlagen.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  return {
    row,
    applies,
    approved,
    inReview,
    checks,
    openWarnings,
    blocking: hasBlockingChecks(checks),
    firstApprovedByMe,
    busy,
    error,
    info,
    acknowledge: (code: string, note: string) =>
      run('acknowledge-checks', { codes: [{ code, note: note.trim() || null }] }),
    approve: () =>
      run('approve', {}, (payload) =>
        payload?.approval === 'first_approval'
          ? 'Erste Freigabe erteilt. Eine zweite Person muss noch freigeben.'
          : null,
      ),
    revoke: (reason: string) => run('revoke-approval', { reason }),
  };
}

export type IncomingInvoiceReviewState = ReturnType<typeof useIncomingInvoiceReview>;

function CheckRow({
  check,
  acknowledged,
  canAcknowledge,
  busy,
  onAcknowledge,
}: {
  check: InvoiceCheck;
  acknowledged: boolean;
  canAcknowledge: boolean;
  busy: boolean;
  onAcknowledge: (note: string) => void;
}) {
  const [noteOpen, setNoteOpen] = React.useState(false);
  const [note, setNote] = React.useState('');
  const isError = check.severity === 'error';
  const Icon = isError ? XCircle : check.highlight ? ShieldAlert : AlertTriangle;
  const iconTone = isError ? 'text-rose-600' : 'text-amber-600';
  return (
    <li className={`flex gap-2 text-sm ${acknowledged ? 'text-app-text-subtle' : 'text-app-text'}`}>
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${acknowledged ? 'text-app-text-subtle' : iconTone}`} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className={check.highlight && !acknowledged ? 'font-medium' : undefined}>{check.message}</p>
        {!isError && acknowledged ? <p className="mt-0.5 text-xs">Bestätigt</p> : null}
        {!isError && !acknowledged && canAcknowledge ? (
          noteOpen ? (
            <div className="mt-1.5 flex gap-1.5">
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Notiz (optional)"
                className="h-8 min-w-0 flex-1 rounded-md border border-app-border bg-app-surface px-2 text-xs text-app-text outline-none focus:border-pipedrive-blue"
                maxLength={500}
                autoFocus
              />
              <button
                type="button"
                disabled={busy}
                onClick={() => onAcknowledge(note)}
                className="h-8 shrink-0 rounded-md border border-app-border bg-app-surface px-2.5 text-xs font-medium text-app-text hover:bg-app-hover disabled:opacity-60"
              >
                Bestätigen
              </button>
            </div>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => setNoteOpen(true)}
              className="mt-0.5 text-xs font-medium text-pipedrive-blue hover:underline disabled:opacity-60"
            >
              Geprüft, bestätigen
            </button>
          )
        ) : null}
      </div>
    </li>
  );
}

/** Befunde der Plausibilitätsprüfung in der Kopfleiste (eigener Scrollbereich). Ohne Befunde: nichts. */
export function IncomingInvoiceReviewFindings({
  review,
  canApprove,
}: {
  review: IncomingInvoiceReviewState;
  canApprove: boolean;
}) {
  if (!review.applies || review.approved || review.checks.length === 0) return null;
  const errors = review.checks.filter((c) => c.severity === 'error');
  const warnings = review.checks.filter((c) => c.severity === 'warning');
  return (
    <section
      aria-label="Prüfergebnis"
      className="app-scrollbar max-h-[35vh] overflow-y-auto overscroll-contain border-t border-app-border px-4 py-3"
    >
      <ul className="space-y-2">
        {[...errors, ...warnings].map((check) => (
          <CheckRow
            key={check.code}
            check={check}
            acknowledged={check.severity === 'warning' && !review.openWarnings.some((w) => w.code === check.code)}
            canAcknowledge={canApprove}
            busy={review.busy}
            onAcknowledge={(note) => { void review.acknowledge(check.code, note); }}
          />
        ))}
      </ul>
    </section>
  );
}

/** Kopfleiste der Seitenleiste: Stand links, Aktionen rechts. Bleibt beim Scrollen sichtbar. */
export function IncomingInvoiceReviewBar({
  review,
  canApprove,
  saving,
  onReject,
}: {
  review: IncomingInvoiceReviewState;
  canApprove: boolean;
  saving: boolean;
  onReject: () => void;
}) {
  const [revokeOpen, setRevokeOpen] = React.useState(false);
  const [revokeReason, setRevokeReason] = React.useState('');
  React.useEffect(() => {
    setRevokeOpen(false);
    setRevokeReason('');
  }, [review.row.id]);

  if (!review.applies) return null;
  const { row } = review;
  const disabled = review.busy || saving;
  const approveBlockedReason = review.blocking
    ? 'Erst die Fehler korrigieren.'
    : review.openWarnings.length > 0
      ? 'Erst alle Hinweise bestätigen.'
      : review.firstApprovedByMe
        ? 'Die zweite Freigabe muss eine andere Person erteilen.'
        : null;
  // Kurzform für die Statuszeile; die Befunde stehen direkt darunter.
  const errorCount = review.checks.filter((c) => c.severity === 'error').length;
  const blockedShort = review.blocking
    ? `${errorCount} Fehler offen`
    : review.openWarnings.length > 0
      ? `${review.openWarnings.length} ${review.openWarnings.length === 1 ? 'Hinweis' : 'Hinweise'} offen`
      : review.firstApprovedByMe
        ? 'Zweite Person nötig'
        : null;

  // Vor Einführung der Freigabe automatisch gebucht: nicht als „Freigegeben“ anzeigen (keine Prüfung),
  // aber auch nicht eigens benennen. Die Kennzeichnung bleibt in der Datenbank.
  const legacyAutoBooked = review.approved && row.approval_note === LEGACY_AUTO_BOOKED_NOTE && !row.approved_by;
  const status = legacyAutoBooked ? null : review.approved ? (
    <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
      Freigegeben {formatDate(row.approved_at)}
    </span>
  ) : row.approval_first_by ? (
    <span>Zweite Freigabe nötig</span>
  ) : blockedShort && canApprove ? (
    <span className={review.blocking ? 'text-rose-700 dark:text-rose-400' : 'text-amber-700 dark:text-amber-400'}>{blockedShort}</span>
  ) : (
    <span className="text-app-text-muted">In Prüfung</span>
  );

  return (
    <div className="space-y-2 px-4 py-3">
      {review.error ? <p className="text-xs text-rose-700 dark:text-rose-400" role="alert">{review.error}</p> : null}
      {review.info ? <p className="text-xs text-emerald-700 dark:text-emerald-400">{review.info}</p> : null}

      {revokeOpen ? (
        <div className="space-y-2">
          <input
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            placeholder="Grund, z. B. Betrag korrigieren"
            className="h-9 w-full rounded-md border border-app-border bg-app-surface px-3 text-sm text-app-text outline-none focus:border-pipedrive-blue"
            maxLength={500}
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={() => setRevokeOpen(false)}
              className="inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-app-text-muted hover:bg-app-hover"
            >
              Abbrechen
            </button>
            <button
              type="button"
              disabled={disabled || !revokeReason.trim()}
              onClick={() => {
                void review.revoke(revokeReason.trim()).then((ok) => { if (ok) setRevokeOpen(false); });
              }}
              className="inline-flex h-9 items-center rounded-md border border-app-border bg-app-surface px-3 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
            >
              Freigabe zurücknehmen
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <p className="min-w-0 truncate text-xs">{status}</p>
          {review.inReview && canApprove ? (
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                disabled={disabled}
                onClick={onReject}
                className="inline-flex h-9 items-center rounded-md border border-app-border bg-app-surface px-3 text-sm font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-50"
              >
                Ablehnen
              </button>
              <button
                type="button"
                disabled={disabled || Boolean(approveBlockedReason)}
                onClick={() => { void review.approve(); }}
                title={approveBlockedReason ?? undefined}
                className="inline-flex h-9 items-center rounded-md bg-pipedrive-blue px-3.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Freigeben
              </button>
            </div>
          ) : null}
          {review.approved && canApprove && row.status !== 'paid' ? (
            <button
              type="button"
              disabled={disabled}
              onClick={() => setRevokeOpen(true)}
              className="shrink-0 text-xs font-medium text-app-text-muted hover:text-app-text hover:underline disabled:opacity-50"
              title="Freigegebene Belege sind festgeschrieben. Für Korrekturen erst die Freigabe zurücknehmen."
            >
              Freigabe zurücknehmen
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
